import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";

const STAGES = ["Enquiry received", "Assessment pending", "Quote prepared", "Quote accepted", "Scheduled",
  "Technician assigned", "Installation in progress", "QC", "Completed", "Warranty activated"];

export default function PartnerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);

  const load = () => api.get("/partner/jobs").then(({ data }) => setJobs(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try { await api.put(`/partner/jobs/${id}/status`, { status }); toast.success(`Status → ${status}`); load(); }
    catch (_) { toast.error("Update failed"); }
  };

  const stats = {
    total: jobs.length,
    active: jobs.filter((j) => !["Completed", "Warranty activated"].includes(j.status)).length,
    completed: jobs.filter((j) => ["Completed", "Warranty activated"].includes(j.status)).length,
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="partner-dashboard-page">
      <Seo title="Partner Dashboard" description="ERRAH partner jobs." />
      <header className="glass sticky top-0 z-30 px-5 lg:px-10 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3"><img src="/errah-logo.png" alt="ERRAH" className="h-9 w-auto object-contain" /><span className="font-display text-xl tracking-[0.2em]">ERRAH · PARTNER</span></div>
        <button onClick={() => { logout(); navigate("/"); }} data-testid="partner-logout" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white"><LogOut size={16} /> Logout</button>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-10">
        <h1 className="font-display text-3xl font-light">{user?.name}</h1>
        <div className="mt-6 grid grid-cols-3 gap-px bg-white/10">
          {[["Jobs", stats.total], ["Active", stats.active], ["Completed", stats.completed]].map(([k, v]) => (
            <div key={k} className="bg-[#0A0A0A] p-6"><p className="errah-label">{k}</p><p className="font-display text-3xl mt-1">{v}</p></div>
          ))}
        </div>

        <h2 className="font-display text-2xl mt-12 mb-6">Assigned jobs</h2>
        {jobs.length === 0 ? <p className="text-gray-500">No jobs assigned yet.</p> : (
          <div className="space-y-4">
            {jobs.map((j) => (
              <div key={j.id} className="hair-border bg-white/[0.02] p-6" data-testid={`job-${j.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display text-lg">{j.service}</h3>
                    <p className="text-sm text-gray-500">{j.customer_name} · {j.surface_type} · {j.location || "—"} · {j.preferred_date || "TBD"} {j.preferred_time || ""}</p>
                    {j.notes && <p className="text-xs text-gray-600 mt-1">{j.notes}</p>}
                  </div>
                  <span className="px-3 py-1 text-xs text-[#C9A45C] border border-[#C9A45C]/40">{j.status}</span>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span className="errah-label">Update status</span>
                  <select defaultValue={j.status} onChange={(e) => updateStatus(j.id, e.target.value)} data-testid={`job-status-${j.id}`}
                    className="bg-[#0A0A0A] border border-white/15 px-3 py-2 text-sm [color-scheme:dark]">
                    {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
